import { createContext, useContext, useState } from 'react';
import { useForm, Controller } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { motion } from 'framer-motion';
import { toast } from 'sonner';
import { Loader2, CheckCircle2, X } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from '../ui/dialog';
import { Input } from '../ui/input';
import { Textarea } from '../ui/textarea';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../ui/select';
import { submitQuote } from '../../lib/api';
import { quoteSchema } from '../../lib/schemas';
import { CATEGORIES } from '../../data/content';

const QuoteCtx = createContext(null);
export const useQuote = () => useContext(QuoteCtx);

const FieldLabel = ({ children }) => (
  <label className="mb-1.5 block text-sm font-medium text-mt-ink">{children}</label>
);

export const QuoteProvider = ({ children }) => {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);
  const [product, setProduct] = useState(null);
  const {
    register,
    handleSubmit,
    control,
    reset,
    formState: { errors },
  } = useForm({
    resolver: zodResolver(quoteSchema),
    defaultValues: {
      name: '',
      email: '',
      company: '',
      country: '',
      category: '',
      message: '',
      enquiry_type: 'general',
      quantity: '25',
      incoterm: 'FOB',
      destination_port: '',
    },
  });

  const openQuote = (selectedProduct = null) => {
    setProduct(selectedProduct && selectedProduct.slug ? selectedProduct : null);
    setDone(false);
    setOpen(true);
  };

  const onSubmit = async (values) => {
    setLoading(true);
    try {
      await submitQuote({ ...values, attached_product_slug: product?.slug });
      setDone(true);
      reset();
      toast.success(t('quote.sent_title'));
    } catch {
      toast.error('Failed to submit quote. Try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <QuoteCtx.Provider value={{ openQuote }}>
      {children}
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent
          data-testid="quote-modal"
          className="max-w-2xl max-h-[90vh] overflow-y-auto rounded-3xl p-0 gap-0 border-0 bg-white"
        >
          <DialogHeader className="bg-gradient-to-r from-mt-blue to-mt-sky px-8 pt-8 pb-7 text-white rounded-t-3xl">
            <DialogTitle className="font-display text-2xl font-extrabold text-white">
              {t('quote.title')}
            </DialogTitle>
            <DialogDescription className="text-white/80 mt-1 text-sm">
              {t('quote.subtitle')}
            </DialogDescription>
          </DialogHeader>

          {done ? (
            <div className="p-10 text-center">
              <motion.div
                initial={{ scale: 0.8, opacity: 0 }}
                animate={{ scale: 1, opacity: 1 }}
                className="mx-auto flex h-16 w-16 items-center justify-center rounded-full bg-emerald-100 text-emerald-600 mb-4"
              >
                <CheckCircle2 className="h-9 w-9" strokeWidth={1.5} />
              </motion.div>
              <h3 className="font-display text-xl font-bold text-mt-ink">{t('quote.sent_title')}</h3>
              <p className="mt-2 text-mt-muted">
                {t('quote.sent_desc')}
              </p>
              <button
                data-testid="quote-close-btn"
                onClick={() => setOpen(false)}
                className="mt-6 rounded-full bg-mt-blue px-8 py-3 font-display font-semibold text-white transition-colors hover:bg-mt-sky"
              >
                {t('quote.close')}
              </button>
            </div>
          ) : (
            <form onSubmit={handleSubmit(onSubmit)} noValidate className="px-8 py-7 space-y-4">
              {product && (
                <div
                  data-testid="quote-product"
                  className="flex items-center justify-between rounded-xl bg-slate-50 border border-slate-200/60 p-3 text-sm text-mt-ink"
                >
                  <div className="flex items-center gap-2">
                    <span className="font-semibold text-mt-blue">Attached Product:</span>
                    <span>{product.name}</span>
                  </div>
                  <button
                    type="button"
                    onClick={() => setProduct(null)}
                    className="p-1 rounded-full hover:bg-slate-200 text-slate-500 hover:text-slate-700"
                    aria-label="Remove product"
                  >
                    <X className="h-4 w-4" />
                  </button>
                </div>
              )}

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <FieldLabel>{t('quote.name')}</FieldLabel>
                  <Input data-testid="quote-name" {...register('name')} placeholder={t('quote.name_placeholder')} className="rounded-xl" />
                  {errors.name && <p className="mt-1 text-xs text-red-500">{errors.name.message}</p>}
                </div>
                <div>
                  <FieldLabel>{t('quote.email')}</FieldLabel>
                  <Input data-testid="quote-email" type="email" {...register('email')} placeholder={t('quote.email_placeholder')} className="rounded-xl" />
                  {errors.email && <p className="mt-1 text-xs text-red-500">{errors.email.message}</p>}
                </div>
                <div>
                  <FieldLabel>{t('quote.company')}</FieldLabel>
                  <Input data-testid="quote-company" {...register('company')} placeholder={t('quote.company')} className="rounded-xl" />
                </div>
                <div>
                  <FieldLabel>{t('quote.country')}</FieldLabel>
                  <Input data-testid="quote-country" {...register('country')} placeholder={t('quote.country')} className="rounded-xl" />
                </div>
                <div>
                  <FieldLabel>{t('quote.enquiry_type')}</FieldLabel>
                  <Controller
                    name="enquiry_type"
                    control={control}
                    render={({ field }) => (
                      <Select value={field.value} onValueChange={field.onChange}>
                        <SelectTrigger className="rounded-xl">
                          <SelectValue placeholder="General Inquiry" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="general">General Inquiry</SelectItem>
                          <SelectItem value="bulk">Bulk Purchase</SelectItem>
                          <SelectItem value="distributor">Distributor Request</SelectItem>
                        </SelectContent>
                      </Select>
                    )}
                  />
                </div>
                <div>
                  <FieldLabel>{t('quote.incoterm')}</FieldLabel>
                  <Controller
                    name="incoterm"
                    control={control}
                    render={({ field }) => (
                      <Select value={field.value} onValueChange={field.onChange}>
                        <SelectTrigger className="rounded-xl">
                          <SelectValue placeholder="FOB" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="EXW">EXW – Ex Works</SelectItem>
                          <SelectItem value="FOB">FOB – Free On Board</SelectItem>
                          <SelectItem value="CIF">CIF – Cost, Insurance & Freight</SelectItem>
                          <SelectItem value="DDP">DDP – Delivered Duty Paid</SelectItem>
                          <SelectItem value="CPT">CPT – Carriage Paid To</SelectItem>
                        </SelectContent>
                      </Select>
                    )}
                  />
                </div>
                <div>
                  <FieldLabel>Destination Port</FieldLabel>
                  <Input {...register('destination_port')} placeholder="Hamburg Port" className="rounded-xl" />
                </div>
              </div>
              <div>
                <FieldLabel>Product Category</FieldLabel>
                <Controller
                  name="category"
                  control={control}
                  render={({ field }) => (
                    <Select value={field.value || undefined} onValueChange={field.onChange}>
                      <SelectTrigger data-testid="quote-category" className="rounded-xl">
                        <SelectValue placeholder={product?.category || "Select a category"} />
                      </SelectTrigger>
                      <SelectContent>
                        {CATEGORIES.map((c) => (
                          <SelectItem key={c.name} value={c.name}>
                            {c.name}
                          </SelectItem>
                        ))}
                        <SelectItem value="OEM / Private Label">OEM / Private Label</SelectItem>
                      </SelectContent>
                    </Select>
                  )}
                />
              </div>
              <div>
                <FieldLabel>Message *</FieldLabel>
                <Textarea
                  data-testid="quote-message"
                  {...register('message')}
                  placeholder="Describe the instruments, quantities and specifications you need…"
                  className="rounded-xl min-h-[110px]"
                />
                {errors.message && <p className="mt-1 text-xs text-red-500">{errors.message.message}</p>}
              </div>
              <button
                data-testid="quote-submit"
                type="submit"
                disabled={loading}
                className="flex w-full items-center justify-center gap-2 rounded-full bg-mt-orange px-8 py-3.5 font-display font-semibold text-white shadow-[0_8px_32px_rgba(235,93,11,0.25)] transition-all duration-300 hover:bg-mt-orangeLight hover:-translate-y-0.5 disabled:opacity-60"
              >
                {loading ? <Loader2 className="h-5 w-5 animate-spin" /> : 'Submit Request'}
              </button>
            </form>
          )}
        </DialogContent>
      </Dialog>
    </QuoteCtx.Provider>
  );
};
